/**
 * Hover-to-explain for input fields. Pausing over a field for a moment reveals
 * a fuller description than the one-line hint below it — no icon to hunt for,
 * nothing to click. Keyboard focus opens it too, so it isn't mouse-only.
 */
import { useEffect, useRef, useState } from 'react';

const OPEN_DELAY_MS = 400;

export function useFieldTip(text?: string) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  const cancel = () => {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => cancel, []);

  if (!text) return { handlers: {}, bubble: null };

  const openSoon = () => {
    cancel();
    timer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  };
  const close = () => {
    cancel();
    setOpen(false);
  };

  return {
    handlers: {
      onMouseEnter: openSoon,
      onMouseLeave: close,
      // Keyboard and touch users get it immediately rather than on a timer.
      onFocus: () => { cancel(); setOpen(true); },
      onBlur: close,
    },
    bubble: open ? <span className="field-tip" role="tooltip">{text}</span> : null,
  };
}
